import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Badge, 
    Modal, Spinner, Alert, Tooltip, OverlayTrigger, ProgressBar, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatDate } from "../utils/formatters";
import { MODE_REGLEMENT } from "../utils/constantes";
import './Factures.css';

export default function FacturesImpayees() {
    const navigate = useNavigate();
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [stats, setStats] = useState(null);
    const [showPaiementModal, setShowPaiementModal] = useState(false);
    const [factureSelectionnee, setFactureSelectionnee] = useState(null);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_echeance', direction: 'asc' });
    
    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    const [paiement, setPaiement] = useState({
        montant: '',
        mode_reglement: 'especes',
        reference: '',
        date_reglement: new Date().toISOString().split('T')[0]
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchImpayees();
        fetchStats();
    }, []);

    const fetchImpayees = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?action=impayees',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFactures(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php?action=stats',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur stats", err);
        }
    };

    const showSuccess = (message) => {
        setSuccessMessage(message);
        setShowSuccessAlert(true);
        setTimeout(() => setShowSuccessAlert(false), 3000);
    };

    const showError = (message) => {
        setErrorMessage(message);
        setShowErrorAlert(true);
        setTimeout(() => setShowErrorAlert(false), 3000);
    };

    const handlePaiementSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            if (!user || !user.id) {
                showError("Utilisateur non identifié");
                setSaving(false);
                return;
            }

            console.log("Envoi paiement avec utilisateur:", user.id);

            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/factures.php',
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'encaisser',
                        id_facture: factureSelectionnee.id_facture,
                        montant: parseFloat(paiement.montant),
                        mode_reglement: paiement.mode_reglement,
                        reference: paiement.reference,
                        id_utilisateur: user.id
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowPaiementModal(false);
                fetchImpayees();
                fetchStats();
                showSuccess("Paiement enregistré avec succès");
                
                setPaiement({
                    montant: '',
                    mode_reglement: 'especes',
                    reference: '',
                    date_reglement: new Date().toISOString().split('T')[0]
                });
            } else {
                showError(result.message || "Erreur lors du paiement");
            }
        } catch (err) {
            console.error("Erreur paiement:", err);
            showError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const getJoursRetard = (dateEcheance) => {
        const today = new Date();
        const echeance = new Date(dateEcheance);
        const diffTime = today - echeance;
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    };

    const getResteCouleur = (reste, joursRetard) => {
        if (reste === 0) return 'text-success';
        if (joursRetard > 7) return 'text-danger fw-bold';
        if (joursRetard > 0) return 'text-danger';
        return 'text-warning';
    };

    const filteredFactures = useMemo(() => {
        return factures
            .filter(f => 
                f.numero_facture?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.client_nom?.toLowerCase().includes(filterText.toLowerCase()) ||
                f.code_client?.toLowerCase().includes(filterText.toLowerCase())
            )
            .sort((a, b) => {
                const aRetard = getJoursRetard(a.date_echeance) > 0;
                const bRetard = getJoursRetard(b.date_echeance) > 0;
                
                if (aRetard && !bRetard) return -1;
                if (!aRetard && bRetard) return 1;
                
                if (sortConfig.key === 'date_echeance') {
                    return sortConfig.direction === 'asc'
                        ? getJoursRetard(a.date_echeance) - getJoursRetard(b.date_echeance)
                        : getJoursRetard(b.date_echeance) - getJoursRetard(a.date_echeance);
                }
                if (sortConfig.key === 'montant') {
                    const resteA = a.montant_ttc - (a.montant_regle || 0);
                    const resteB = b.montant_ttc - (b.montant_regle || 0);
                    return sortConfig.direction === 'asc' ? resteA - resteB : resteB - resteA;
                }
                return 0;
            });
    }, [factures, filterText, sortConfig]);

    // ✅ PAGINATION - Placée ici après filteredFactures
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredFactures.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredFactures.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    // ✅ MAINTENANT currentItems est disponible
    const totalImpaye = currentItems.reduce((acc, f) => 
        acc + (f.montant_ttc - (f.montant_regle || 0)), 0
    );

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            onClick={() => handleSort(field)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            className="sortable-header"
        >
            <div className="d-flex align-items-center gap-1">
                {children}
                {sortConfig.key === field && (
                    <i className={`ri-arrow-${sortConfig.direction === 'asc' ? 'up' : 'down'}-s-line`}></i>
                )}
            </div>
        </th>
    );

    const ActionButton = ({ variant, icon, onClick, tooltip, disabled = false }) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${variant}`}>{tooltip}</Tooltip>}
        >
            <Button
                variant={variant}
                size="sm"
                onClick={onClick}
                disabled={disabled}
                className="action-btn"
            >
                <i className={icon}></i>
            </Button>
        </OverlayTrigger>
    );

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="loading-container">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement des factures impayées...</p>
                        </div>
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if (error) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="text-center py-5">
                        <i className="ri-error-warning-line fs-48 mb-3 d-block"></i>
                        <h4>{error}</h4>
                        <Button 
                            variant="outline-danger" 
                            className="mt-3"
                            onClick={fetchImpayees}
                        >
                            <i className="ri-refresh-line me-2"></i>
                            Réessayer
                        </Button>
                    </Alert>
                </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* Alertes */}
                    {showSuccessAlert && (
                        <Alert variant="success" onClose={() => setShowSuccessAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {successMessage}
                        </Alert>
                    )}
                    
                    {showErrorAlert && (
                        <Alert variant="danger" onClose={() => setShowErrorAlert(false)} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {errorMessage}
                        </Alert>
                    )}

                    {/* En-tête */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="page-icon bg-danger-soft">
                                        <i className="ri-error-warning-line text-danger"></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1">Factures impayées</h1>
                                        <p className="page-subtitle mb-0">
                                            Gérez les paiements en retard et les impayés
                                        </p>
                                    </div>
                                </div>
                                <Button 
                                    variant="primary" 
                                    onClick={() => navigate('/ventes/factures')}
                                    className="btn-gradient px-4 py-2"
                                >
                                    <i className="ri-arrow-left-line me-2"></i>
                                    Toutes les factures
                                </Button>
                            </div>
                        </Col>
                    </Row>

                    {/* Statistiques */}
                    <Row className="g-3 mb-4 stats-row">
                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0">
                                <Card.Body>
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-danger">
                                            <i className="ri-error-warning-line text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Total impayés</span>
                                            <h3 className="stat-value">{formatCurrency(totalImpaye)}</h3>
                                            <small className="stat-trend text-danger">
                                                <i className="ri-file-list-3-line me-1"></i>
                                                {filteredFactures.length} facture(s)
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0">
                                <Card.Body>
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-warning">
                                            <i className="ri-timer-line text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Retard maximum</span>
                                            <h3 className="stat-value">
                                                {currentItems.length > 0 
                                                    ? Math.max(...currentItems.map(f => {
                                                        const jours = getJoursRetard(f.date_echeance);
                                                        return jours > 0 ? jours : 0;
                                                      }))
                                                    : 0} <small style={{fontSize: '0.9rem'}}>jours</small>
                                            </h3>
                                            <small className="stat-trend text-warning">
                                                <i className="ri-alert-line me-1"></i>
                                                À traiter urgemment
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0">
                                <Card.Body>
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-info">
                                            <i className="ri-group-line text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Clients concernés</span>
                                            <h3 className="stat-value">{new Set(factures.map(f => f.id_client)).size}</h3>
                                            <small className="stat-trend text-info">
                                                <i className="ri-user-line me-1"></i>
                                                {filteredFactures.length} factures
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col sm={6} lg={3}>
                            <Card className="stat-card border-0">
                                <Card.Body>
                                    <div className="d-flex align-items-center">
                                        <div className="stat-icon-wrapper bg-primary">
                                            <i className="ri-bar-chart-2-line text-white"></i>
                                        </div>
                                        <div className="stat-content">
                                            <span className="stat-label">Moy. par facture</span>
                                            <h3 className="stat-value">
                                                {filteredFactures.length > 0 
                                                    ? formatCurrency(totalImpaye / filteredFactures.length)
                                                    : formatCurrency(0)}
                                            </h3>
                                            <small className="stat-trend text-primary">
                                                <i className="ri-bar-chart-2-line me-1"></i>
                                                Montant moyen
                                            </small>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Filtres */}
                    <Card className="filter-card border-0 mb-4">
                        <Card.Body>
                            <Row className="g-3">
                                <Col md={6}>
                                    <InputGroup className="search-input">
                                        <InputGroup.Text>
                                            <i className="ri-search-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            placeholder="Rechercher facture, client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                        />
                                        {filterText && (
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => setFilterText('')}
                                                className="border-0"
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={6} className="d-flex justify-content-end align-items-center">
                                    <div className="filter-info">
                                        <span className="badge-count">{filteredFactures.length}</span>
                                        <span className="text-muted ms-2">facture(s) impayée(s)</span>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Liste des factures impayées */}
                    <Card className="table-card border-0">
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead>
                                        <tr>
                                            <th>N° Facture</th>
                                            <th>Date</th>
                                            <th>Client</th>
                                            <SortableHeader field="date_echeance">Échéance</SortableHeader>
                                            <th className="text-end">Montant</th>
                                            <th className="text-end">Réglé</th>
                                            <SortableHeader field="montant">Reste</SortableHeader>
                                            <th>Progression</th>
                                            <th>Retard</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((facture) => {
                                                const reste = facture.montant_ttc - (facture.montant_regle || 0);
                                                const joursRetard = getJoursRetard(facture.date_echeance);
                                                const estEnRetard = joursRetard > 0;
                                                const progression = (facture.montant_regle / facture.montant_ttc) * 100;
                                                
                                                return (
                                                    <tr 
                                                        key={facture.id_facture} 
                                                        className={`facture-row ${estEnRetard ? 'en-retard' : ''}`}
                                                    >
                                                        <td>
                                                            <Link 
                                                                to={`/ventes/factures/${facture.id_facture}`} 
                                                                className="facture-link"
                                                                title={facture.numero_facture}
                                                            >
                                                                <span className="facture-num">
                                                                    {facture.numero_facture?.length > 8 
                                                                        ? facture.numero_facture.substring(0, 8) + '...' 
                                                                        : facture.numero_facture}
                                                                </span>
                                                            </Link>
                                                        </td>
                                                        <td>
                                                            <span className="facture-date">
                                                                {formatDate(facture.date_facture)}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="client-info">
                                                                <span className="client-name">{facture.client_nom}</span>
                                                                <small className="client-code">{facture.code_client}</small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="echeance-info">
                                                                <span className={`echeance-date ${estEnRetard ? 'text-danger fw-bold' : ''}`}>
                                                                    {formatDate(facture.date_echeance)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="montant-ttc fw-bold">
                                                                {formatCurrency(facture.montant_ttc)}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="montant-regle text-success">
                                                                {formatCurrency(facture.montant_regle || 0)}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className={`montant-reste fw-bold ${getResteCouleur(reste, joursRetard)}`}>
                                                                {formatCurrency(reste)}
                                                            </span>
                                                        </td>
                                                        <td style={{ minWidth: '100px' }}>
                                                            <div className="d-flex flex-column">
                                                                <ProgressBar 
                                                                    now={progression} 
                                                                    variant={progression === 100 ? 'success' : 'warning'} 
                                                                    style={{ height: '6px', width: '100px' }}
                                                                />
                                                                <small className="text-muted mt-1">
                                                                    {Math.round(progression)}% payé
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            {estEnRetard ? (
                                                                <Badge bg="danger" className="px-3 py-2">
                                                                    <i className="ri-alarm-warning-line me-1"></i>
                                                                    J+{joursRetard}
                                                                </Badge>
                                                            ) : (
                                                                <Badge bg="success" className="px-3 py-2">
                                                                    <i className="ri-check-line me-1"></i>
                                                                    J-{Math.abs(joursRetard)}
                                                                </Badge>
                                                            )}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="actions-container">
                                                                <ActionButton
                                                                    variant="outline-info"
                                                                    icon="ri-eye-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Voir détails"
                                                                />
                                                                <ActionButton
                                                                    variant="outline-success"
                                                                    icon="ri-bank-card-line"
                                                                    onClick={() => {
                                                                        setFactureSelectionnee(facture);
                                                                        setPaiement({
                                                                            ...paiement,
                                                                            montant: reste
                                                                        });
                                                                        setShowPaiementModal(true);
                                                                    }}
                                                                    tooltip="Encaisser"
                                                                />
                                                                <ActionButton
                                                                    variant="outline-warning"
                                                                    icon="ri-file-copy-line"
                                                                    onClick={() => navigate(`/ventes/facturesDetails/${facture.id_facture}`)}
                                                                    tooltip="Créer un avoir"
                                                                />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="10" className="text-center py-5">
                                                    <div className="empty-state">
                                                        <i className="ri-checkbox-circle-line empty-icon text-success"></i>
                                                        <h5 className="mt-3">Aucune facture impayée</h5>
                                                        <p className="text-muted mb-3">Toutes les factures sont soldées</p>
                                                        {filterText && (
                                                            <Button 
                                                                variant="outline-primary"
                                                                onClick={() => setFilterText('')}
                                                            >
                                                                <i className="ri-refresh-line me-2"></i>
                                                                Réinitialiser la recherche
                                                            </Button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, filteredFactures.length)} sur {filteredFactures.length} factures
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
                <Footer />
            </div>

            {/* Modal de paiement */}
            <Modal show={showPaiementModal} onHide={() => setShowPaiementModal(false)} centered className="modern-modal">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="ri-bank-card-line me-2 text-success"></i>
                        Enregistrer un paiement
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handlePaiementSubmit}>
                    <Modal.Body>
                        {factureSelectionnee && (
                            <>
                                <Alert variant="info" className="mb-3 alert-modern">
                                    <div className="d-flex justify-content-between align-items-center w-100">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="bg-primary bg-opacity-10 p-2 rounded-circle">
                                                <i className="ri-file-list-3-line text-primary"></i>
                                            </div>
                                            <div>
                                                <strong>Facture {factureSelectionnee.numero_facture}</strong>
                                                <div className="small text-muted">
                                                    <i className="ri-user-line me-1"></i>
                                                    {factureSelectionnee.client_nom}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <span className="badge bg-danger text-white p-3 rounded-pill">
                                                <i className="ri-money-euro-circle-line me-2"></i>
                                                Reste: {formatCurrency(factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0))}
                                            </span>
                                        </div>
                                    </div>
                                </Alert>

                                <Form.Group className="mb-3">
                                    <Form.Label>Date de paiement</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={paiement.date_reglement}
                                        onChange={(e) => setPaiement({...paiement, date_reglement: e.target.value})}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Montant</Form.Label>
                                    <InputGroup>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            min="0.01"
                                            max={factureSelectionnee.montant_ttc - (factureSelectionnee.montant_regle || 0)}
                                            value={paiement.montant}
                                            onChange={(e) => setPaiement({...paiement, montant: e.target.value})}
                                            required
                                        />
                                        <InputGroup.Text>FCFA</InputGroup.Text>
                                    </InputGroup>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Mode de règlement</Form.Label>
                                    <Form.Select
                                        value={paiement.mode_reglement}
                                        onChange={(e) => setPaiement({...paiement, mode_reglement: e.target.value})}
                                        required
                                    >
                                        {MODE_REGLEMENT.map(mode => (
                                            <option key={mode.value} value={mode.value}>
                                                {mode.icon} {mode.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Référence (optionnel)</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="N° chèque, référence virement..."
                                        value={paiement.reference}
                                        onChange={(e) => setPaiement({...paiement, reference: e.target.value})}
                                    />
                                </Form.Group>
                            </>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPaiementModal(false)}>
                            Annuler
                        </Button>
                        <Button variant="success" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Enregistrement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-check-line me-2"></i>
                                    Enregistrer le paiement
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <style>{`
                .fade-in {
                    animation: fadeIn 0.5s ease-out;
                }
                
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .header-section {
                    background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
                    padding: 1.5rem;
                    border-radius: 20px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }

                .page-icon {
                    width: 60px;
                    height: 60px;
                    background: linear-gradient(135deg, #4361ee20, #80b3ff20);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 2rem;
                }

                .page-title {
                    font-size: 1.8rem;
                    font-weight: 700;
                    color: #1e293b;
                    margin: 0;
                }

                .page-subtitle {
                    color: #64748b;
                    font-size: 0.95rem;
                }

                .btn-gradient {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    border: none;
                    color: white;
                    transition: all 0.3s ease;
                }

                .btn-gradient:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 16px #4361ee40;
                }

                .stats-row {
                    margin-top: 1.5rem;
                }

                .stat-card {
                    border-radius: 20px !important;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    background: white;
                    border: none;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important;
                }

                .stat-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 12px 24px rgba(0,0,0,0.1) !important;
                }

                .stat-icon-wrapper {
                    width: 60px;
                    height: 60px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    margin-right: 1rem;
                }

                .stat-icon-wrapper.bg-primary { background: linear-gradient(135deg, #4361ee, #3a56d4); }
                .stat-icon-wrapper.bg-success { background: linear-gradient(135deg, #10b981, #059669); }
                .stat-icon-wrapper.bg-warning { background: linear-gradient(135deg, #f59e0b, #d97706); }
                .stat-icon-wrapper.bg-danger { background: linear-gradient(135deg, #dc3545, #bb2d3b); }
                .stat-icon-wrapper.bg-info { background: linear-gradient(135deg, #3b82f6, #2563eb); }

                .stat-content {
                    flex: 1;
                }

                .stat-label {
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                    color: #64748b;
                    text-transform: uppercase;
                }

                .stat-value {
                    font-size: 1.2rem;
                    font-weight: 700;
                    line-height: 1.2;
                    margin-bottom: 0.25rem;
                }

                .stat-trend {
                    font-size: 0.8rem;
                    display: flex;
                    align-items: center;
                    gap: 0.25rem;
                }

                @media (max-width: 768px) {
                    .stat-icon-wrapper {
                        width: 50px;
                        height: 50px;
                    }
                    
                    .stat-value {
                        font-size: 1rem;
                    }
                    
                    .stat-icon-wrapper i {
                        font-size: 1.5rem !important;
                    }
                }

                .filter-card {
                    border-radius: 16px !important;
                    background: white;
                }

                .search-input .input-group-text {
                    background: white;
                    border-right: none;
                    color: #64748b;
                }

                .search-input .form-control {
                    border-left: none;
                    padding: 0.6rem 0.75rem;
                }

                .search-input .form-control:focus {
                    border-color: #dee2e6;
                    box-shadow: none;
                }

                .badge-count {
                    background: linear-gradient(135deg, #4361ee, #3a56d4);
                    color: white;
                    padding: 0.3rem 0.8rem;
                    border-radius: 20px;
                    font-weight: 600;
                    font-size: 0.9rem;
                }

                .table-card {
                    border-radius: 16px !important;
                    overflow: hidden;
                    background: white;
                }

                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.85rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem;
                    border-bottom: 2px solid #e2e8f0;
                }

                .sortable-header {
                    cursor: pointer;
                    transition: all 0.2s ease;
                }

                .sortable-header:hover {
                    background: #f1f5f9;
                }

                .custom-table tbody td {
                    padding: 1rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #e2e8f0;
                }

                .facture-row {
                    transition: all 0.2s ease;
                }

                .facture-row:hover {
                    background: #f8fafc !important;
                    transform: scale(1.01);
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                }

                .facture-row.en-retard {
                    background: #fff5f5;
                }

                .facture-link {
                    text-decoration: none;
                    font-weight: 600;
                    color: #1e293b;
                    position: relative;
                }

                .facture-link:hover {
                    color: #4361ee;
                }

                .facture-link::after {
                    content: '';
                    position: absolute;
                    bottom: -2px;
                    left: 0;
                    width: 0;
                    height: 2px;
                    background: #4361ee;
                    transition: width 0.3s ease;
                }

                .facture-link:hover::after {
                    width: 100%;
                }

                .client-info {
                    display: flex;
                    flex-direction: column;
                }

                .client-name {
                    font-weight: 500;
                    color: #1e293b;
                }

                .client-code {
                    color: #64748b;
                    font-size: 0.8rem;
                }

                .actions-container {
                    display: flex;
                    gap: 0.5rem;
                    justify-content: center;
                }

                .action-btn {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }

                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }

                .action-btn.btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }

                .action-btn.btn-outline-success:hover {
                    background: linear-gradient(135deg, #10b981, #059669);
                    border-color: #10b981;
                    color: white;
                }

                .action-btn.btn-outline-warning:hover {
                    background: linear-gradient(135deg, #f59e0b, #d97706);
                    border-color: #f59e0b;
                    color: white;
                }

                .empty-state {
                    padding: 3rem;
                    text-align: center;
                }

                .empty-icon {
                    font-size: 4rem;
                    color: #cbd5e1;
                    margin-bottom: 1.5rem;
                    animation: bounce 2s infinite;
                }

                @keyframes bounce {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }

                .loading-container {
                    padding: 3rem;
                    text-align: center;
                }

                .loading-container .spinner-border {
                    width: 3rem;
                    height: 3rem;
                }

                @media (max-width: 768px) {
                    .page-title {
                        font-size: 1.4rem;
                    }
                    
                    .page-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 1.5rem;
                    }
                    
                    .stat-value {
                        font-size: 1.4rem;
                    }
                    
                    .actions-container {
                        flex-wrap: wrap;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}